<template>
  <main class="public-verification-page">
    <div v-if="loading" class="public-verification-loading">
      <span class="public-verification-spinner" aria-hidden="true"></span>
      Consultando autenticidad...
    </div>

    <section v-else-if="error || notFound" class="public-verification-card">
      <div class="public-verification-icon public-verification-icon--error" aria-hidden="true">×</div>
      <h1>Verificación fallida</h1>
      <p>El código no es válido, expiró o los resultados ya no están disponibles.</p>
      <button type="button" class="public-verification-button" @click="loadVerification">
        Reintentar
      </button>
    </section>

    <section v-else-if="verification && !verification.resultsAvailable" class="public-verification-card">
      <div class="public-verification-icon public-verification-icon--pending" aria-hidden="true">!</div>
      <h1>Verificación pendiente</h1>
      <p>La verificación estará disponible cuando se habiliten automáticamente los resultados.</p>
      <p v-if="verification.metadata.availableAt" class="public-verification-date">
        Disponible desde {{ formatDate(verification.metadata.availableAt) }}.
      </p>
      <button type="button" class="public-verification-button" @click="loadVerification">
        Actualizar
      </button>
    </section>

    <section v-else class="public-verification-card">
      <div
        class="public-verification-icon"
        :class="verification.valid && verification.verified ? 'public-verification-icon--success' : 'public-verification-icon--error'"
        aria-hidden="true"
      >
        {{ verification.valid && verification.verified ? "✓" : "×" }}
      </div>
      <h1>{{ verification.valid && verification.verified ? "Resultado verificado" : "Resultado no verificado" }}</h1>
      <p>
        {{
          verification.valid && verification.verified
            ? "Este código corresponde a un resultado publicado y validado por ASOFOM."
            : "No fue posible validar la integridad de este resultado."
        }}
      </p>
      <span class="public-verification-badge" :class="{ 'public-verification-badge--error': !verification.valid }">
        {{ verification.valid && verification.verified ? "Auténtico" : "No válido" }}
      </span>

      <dl class="public-verification-data">
        <div v-if="verification.metadata.title">
          <dt>Elección</dt>
          <dd>{{ verification.metadata.title }}</dd>
        </div>
        <div v-if="verification.metadata.typeKey">
          <dt>Tipo</dt>
          <dd>{{ getVotingTypeLabel(verification.metadata.typeKey, verification.metadata.typeKey) }}</dd>
        </div>
        <div v-if="verification.metadata.publishedAt">
          <dt>Fecha de publicación</dt>
          <dd>{{ formatDate(verification.metadata.publishedAt) }}</dd>
        </div>
        <div v-if="verification.metadata.hash">
          <dt>Hash</dt>
          <dd><code>{{ verification.metadata.hash }}</code></dd>
        </div>
        <div v-if="verification.metadata.integrityHash">
          <dt>Huella de integridad</dt>
          <dd><code>{{ verification.metadata.integrityHash }}</code></dd>
        </div>
      </dl>

      <router-link
        v-if="verification.valid && verification.verified"
        class="public-verification-button"
        :to="resultsRoute"
      >
        Ver resultados publicados
      </router-link>
    </section>
  </main>
</template>

<script>
import { votingService } from "@/api/voting";
import { formatVotingDate, getVotingTypeLabel } from "@/share/voting";
import { normalizeVotingVerification } from "@/share/votingResults";

export default {
  name: "PublicVotingVerification",
  data() {
    return {
      verification: null,
      loading: true,
      error: false,
      notFound: false,
    };
  },
  computed: {
    resultsRoute() {
      return {
        name: "Public Voting Results",
        params: { shareToken: this.$route.params.shareToken },
      };
    },
  },
  created() {
    this.loadVerification();
  },
  methods: {
    getVotingTypeLabel,
    formatDate: formatVotingDate,
    async loadVerification() {
      this.loading = true;
      this.error = false;
      this.notFound = false;
      try {
        const payload = await votingService.verifyPublicResults(
          this.$route.params.shareToken
        );
        this.verification = normalizeVotingVerification(payload);
      } catch (error) {
        this.verification = null;
        this.notFound = error && error.response && error.response.status === 404;
        this.error = !this.notFound;
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
.public-verification-page {
  display: flex;
  min-height: 100vh;
  box-sizing: border-box;
  align-items: center;
  justify-content: center;
  padding: 32px 16px;
  background: #f9fafc;
  color: #1b273e;
  font-family: "Muli", Arial, sans-serif;
}

.public-verification-loading,
.public-verification-card {
  width: min(100%, 620px);
  text-align: center;
}

.public-verification-loading {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  color: #687386;
}

.public-verification-spinner {
  width: 22px;
  height: 22px;
  border: 3px solid #dfe4eb;
  border-top-color: #ffb500;
  border-radius: 50%;
  animation: public-verification-spin 700ms linear infinite;
}

.public-verification-card {
  border: 1px solid #dfe4eb;
  border-radius: 22px;
  padding: 36px 26px;
  background: #ffffff;
  box-shadow: 0 12px 32px rgba(27, 39, 62, 0.08);
}

.public-verification-icon {
  display: flex;
  width: 72px;
  height: 72px;
  align-items: center;
  justify-content: center;
  margin: 0 auto;
  border-radius: 50%;
  font-size: 34px;
  font-weight: 900;
}

.public-verification-icon--success {
  background: #e7f6ea;
  color: #237b37;
}

.public-verification-icon--pending {
  background: #fff3d6;
  color: #a66f00;
}

.public-verification-icon--error {
  background: #fde8e8;
  color: #b42318;
}

.public-verification-card h1 {
  margin: 18px 0 0;
  font-size: 28px;
  line-height: 1.2;
}

.public-verification-card > p {
  max-width: 480px;
  margin: 10px auto 0;
  color: #687386;
  font-size: 14px;
  line-height: 1.6;
}

.public-verification-date {
  color: #1b273e !important;
}

.public-verification-badge {
  display: inline-flex;
  margin-top: 16px;
  border-radius: 999px;
  padding: 6px 10px;
  background: #e7f6ea;
  color: #237b37;
  font-size: 12px;
  font-weight: 800;
}

.public-verification-badge--error {
  background: #fde8e8;
  color: #b42318;
}

.public-verification-data {
  display: grid;
  gap: 10px;
  margin: 24px 0 0;
  border: 1px solid #dfe4eb;
  border-radius: 14px;
  padding: 16px;
  background: #f9fafc;
  text-align: left;
  font-size: 13px;
}

.public-verification-data div {
  display: grid;
  grid-template-columns: 160px minmax(0, 1fr);
  gap: 12px;
}

.public-verification-data dt {
  color: #687386;
  font-weight: 800;
}

.public-verification-data dd {
  min-width: 0;
  margin: 0;
  overflow-wrap: anywhere;
}

.public-verification-data code {
  color: #687386;
  font-size: 11px;
}

.public-verification-button {
  display: inline-flex;
  min-height: 44px;
  align-items: center;
  justify-content: center;
  margin-top: 24px;
  border: 0;
  border-radius: 999px;
  padding: 10px 18px;
  background: #1b273e;
  color: #ffffff;
  font-size: 14px;
  font-weight: 800;
  text-decoration: none;
  cursor: pointer;
}

@keyframes public-verification-spin {
  to { transform: rotate(360deg); }
}

@media (max-width: 560px) {
  .public-verification-card {
    padding: 28px 16px;
  }

  .public-verification-data div {
    grid-template-columns: 1fr;
    gap: 3px;
  }
}
</style>
